export default function FeatureShowcase() {
  return (
    <section id="features" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            EQUIPMENT MANAGEMENT SYSTEM
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Streamline Your <span className="text-teal-500">Operations</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Manage your team's equipment inventory, track orders, and streamline the entire procurement process from one central platform.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left - Dashboard Preview */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 to-teal-600/20 rounded-3xl blur-3xl"></div>
            <div className="relative bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Equipment Dashboard</h3>
                <p className="text-gray-600">Real-time inventory tracking</p>
              </div>
              
              {/* Inventory Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6">
                  <div className="flex items-center space-x-2 text-teal-600 mb-2">
                    <i className="ri-shopping-bag-3-line text-xl"></i>
                    <span className="text-sm font-semibold">Active Orders</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">24</div>
                  <div className="text-sm text-teal-600 font-medium mt-1">In progress</div>
                </div>
                
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <i className="ri-box-3-line text-xl"></i>
                    <span className="text-sm font-semibold">Total Items</span>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">186</div>
                  <div className="text-sm text-gray-600 font-medium mt-1">In inventory</div>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent Orders</h4>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-football-line text-teal-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Riddell SpeedFlex Helmet</div>
                      <div className="text-xs text-gray-500">Order #1847 • 2 hours ago</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-teal-600">$449.99</div>
                    <div className="text-xs text-gray-500">Shipped</div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-shirt-line text-teal-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Nike Elite Jersey Set</div>
                      <div className="text-xs text-gray-500">Order #1846 • 5 hours ago</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-teal-600">$324.99</div>
                    <div className="text-xs text-gray-500">Processing</div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className="ri-basketball-line text-teal-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Wilson Evolution Ball</div>
                      <div className="text-xs text-gray-500">Order #1845 • 1 day ago</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-teal-600">$69.99</div>
                    <div className="text-xs text-gray-500">Delivered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Features List */}
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-line-chart-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Real-Time Tracking</h3>
                <p className="text-gray-600 leading-relaxed">
                  Monitor all equipment orders and inventory levels in real-time. Stay updated on order status and delivery schedules.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-robot-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Automated Processing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Orders are processed automatically with direct shipping from manufacturers. Focus on your team, not paperwork.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Secure & Organized</h3>
                <p className="text-gray-600 leading-relaxed">
                  Every transaction is tracked with full transparency. View detailed reports, order history, and inventory status anytime.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-team-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Team Collaboration</h3>
                <p className="text-gray-600 leading-relaxed">
                  Coordinate equipment needs across your entire organization. Share access with coaches and staff members.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">Simplified Equipment Management</h3>
          <p className="text-teal-50 text-lg mb-8 max-w-2xl mx-auto">
            Everything you need to manage your team's equipment in one centralized platform
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <i className="ri-shopping-cart-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2 text-lg">Easy Ordering</h4>
              <p className="text-teal-100 text-sm">Browse and order equipment from top brands with just a few clicks</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <i className="ri-truck-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2 text-lg">Direct Shipping</h4>
              <p className="text-teal-100 text-sm">Equipment ships directly from manufacturers to your location</p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <i className="ri-dashboard-line text-4xl text-white mb-3"></i>
              <h4 className="text-white font-bold mb-2 text-lg">Centralized Dashboard</h4>
              <p className="text-teal-100 text-sm">Track all orders and inventory from one easy-to-use interface</p>
            </div>
          </div>

          <div className="mt-8">
            <button
              onClick={() => {
                const element = document.getElementById('equipment-store');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Browse Equipment Store</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
